import React, { useState, useEffect } from "react";
import axios from "axios";
import { Flex, Icon, IconButton, SimpleGrid, Spinner, Text } from "@chakra-ui/react";
import { MdCheckCircle, MdWarning, MdError, MdRefresh, MdStorage, MdTimer, MdSpeed } from "react-icons/md";
import Card from "horizon/components/card/Card";
import PerformanceBannerHeader from "./PerformanceBannerHeader";
import PerformanceMetricCard from "./PerformanceMetricCard";

/**
 * Returns icon, color, and label configuration based on performance status.
 * @param {string} status - The performance status ('good', 'slow', or other).
 * @returns {object} Config with icon component, color token, scheme, and label.
 */
function getStatusConfig(status) {
    switch (status) {
        case 'good':
            return { icon: MdCheckCircle, color: "green.500", scheme: "green", label: 'Excellent' };
        case 'slow':
            return { icon: MdWarning, color: "orange.500", scheme: "orange", label: 'Slow' };
        default:
            return { icon: MdError, color: "red.500", scheme: "red", label: 'Issue' };
    }
}

/**
 * Builds the status metric card config with a colored badge.
 * @param {object} statusConfig - Config with icon, color, scheme, and label
 * @returns {object} Metric card props for the status indicator
 */
function buildStatusMetric(statusConfig) {
    return {
        icon: statusConfig.icon, iconColor: statusConfig.color, label: "Status",
        chipScheme: statusConfig.scheme, chipLabel: statusConfig.label,
    };
}

/**
 * Builds the array of metric card props from performance data.
 * @param {object} data - The raw performance API response.
 * @returns {Array<object>} Array of props objects for PerformanceMetricCard.
 */
function buildMetricCards(data) {
    const statusConfig = getStatusConfig(data?.elasticsearch_performance?.status);
    const queryTime = data?.elasticsearch_performance?.simple_query_time_seconds || 0;
    const indexSize = data?.index_info?.size_human || '0 MB';
    const cachedSearches = data?.cache_status?.total_cached_searches || 0;
    return [
        buildStatusMetric(statusConfig),
        { icon: MdTimer, iconColor: "blue.400", label: "Query time", value: `${(queryTime * 1000).toFixed(0)}ms` },
        { icon: MdStorage, iconColor: "purple.500", label: "Index size", value: indexSize },
        { icon: MdSpeed, iconColor: "orange.500", label: "Cached searches", value: cachedSearches },
    ];
}

/**
 * Fetches performance data from the ElasticSearch debug endpoint.
 * @param {Function} setLoading - Loading state setter
 * @param {Function} setError - Error state setter
 * @param {Function} setData - Data state setter
 * @returns {Promise<void>}
 */
async function fetchPerformanceData(setLoading, setError, setData) {
    setLoading(true);
    setError(null);
    try {
        const response = await axios.get('/service_search/debug/performance');
        setData(response.data);
    } catch (err) {
        console.error("Error fetching performance:", err);
        setError("Unable to retrieve performance data");
    } finally {
        setLoading(false);
    }
}

/**
 * Renders an error state with message and refresh button.
 * @param {object} props
 * @param {string} props.error - Error message to display.
 * @param {Function} props.onRefresh - Callback to retry fetching data.
 * @returns {React.ReactElement}
 */
function PerformanceBannerError({ error, onRefresh }) {
    return (
        <Card>
            <Flex align="center" justify="space-between" gap="12px">
                <Flex align="center" gap="8px">
                    <Icon as={MdError} w="22px" h="22px" color="red.500" />
                    <Text fontSize="sm" color="secondaryGray.600">{error}</Text>
                </Flex>
                <IconButton aria-label="Retry" icon={<Icon as={MdRefresh} />} onClick={onRefresh} variant="ghost" size="sm" />
            </Flex>
        </Card>
    );
}

/**
 * Renders the banner with header and metric cards grid.
 * @param {object} props
 * @param {object} props.data - Performance API response data
 * @param {Function} props.onRefresh - Refresh callback
 * @returns {React.ReactElement}
 */
function PerformanceBannerContent({ data, onRefresh }) {
    const metrics = buildMetricCards(data);
    return (
        <Card>
            <PerformanceBannerHeader onRefresh={onRefresh} />
            <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} spacing="14px">
                {metrics.map((metric) => (
                    <PerformanceMetricCard key={metric.label} {...metric} />
                ))}
            </SimpleGrid>
        </Card>
    );
}

/**
 * ElasticPerformanceBanner displays real-time ElasticSearch performance metrics.
 * Shows status, query time, index size, and cached search count.
 * @param {object} props
 * @param {*} props.refreshTrigger - Value change triggers a data refetch.
 * @returns {React.ReactElement}
 */
function ElasticPerformanceBanner({ refreshTrigger }) {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchPerformanceData(setLoading, setError, setData);
    }, [refreshTrigger]);

    if (loading) return <Card><Flex justify="center" py="32px"><Spinner color="brand.500" /></Flex></Card>;
    if (error) return <PerformanceBannerError error={error} onRefresh={() => fetchPerformanceData(setLoading, setError, setData)} />;
    return <PerformanceBannerContent data={data} onRefresh={() => fetchPerformanceData(setLoading, setError, setData)} />;
}

export default ElasticPerformanceBanner;
